import { getSubscriberPageForEnrollment } from 'api/operations/electionService';
import { requestApplyUserAction } from 'api/operations/enrollmentService';
import { action, computed, makeObservable, observable, override, runInAction } from 'mobx';
import ModalDelegate from 'store/general/modalDelegate';
import { SubscribersFilter, SubscribersSortBy, SubscribersSortOrder } from 'types/electionService';
import { USER_ACTIONS } from 'types/enrollmentService';

export interface BypassModalDelegateParams {
  groupId: string;
  periodId: string;
  onRedirect: () => void;
}

export class BypassModalDelegate extends ModalDelegate {
  private readonly _groupId: string;
  private readonly _periodId: string;
  private readonly _onRedirect: () => void;
  private _totalEmployees: number | null = null;
  private _isLoading: boolean = false;

  public get isModalLoading(): boolean {
    return this._isLoading;
  }

  public get totalEmployees(): number {
    return this._totalEmployees ?? 0;
  }

  public get showBypassBlockedAlert(): boolean {
    if (this._isLoading || this._totalEmployees === null) {
      return false;
    }

    return this._totalEmployees > 0;
  }

  private _setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  private _resetEmployeeCount(): void {
    this._totalEmployees = null;
  }

  public override open(): void {
    super.open();
    void this.loadEmployeeCount();
  }

  public override close(): void {
    super.close();
    this._resetEmployeeCount();
    this._setIsLoading(false);
  }

  public async loadEmployeeCount(): Promise<void> {
    this._setIsLoading(true);
    // The total employee count determines whether the broker can bypass the step.
    // We cannot rely on the page store total because applied filters may affect the value.
    // Here we fetch employees using default filters to get the actual total count.
    const { totalElements } = await getSubscriberPageForEnrollment({
      groupId: this._groupId,
      periodId: this._periodId,
      sortBy: SubscribersSortBy.NAME,
      sortOrder: SubscribersSortOrder.DESC,
      filter: SubscribersFilter.ALL,
      status: [],
      employeeClasses: [],
      progress: [],
      name: '',
      page: 1,
    });

    runInAction(() => {
      this._totalEmployees = totalElements;
      this._setIsLoading(false);
    });
  }

  public async confirmBypass(): Promise<void> {
    if (this._isLoading || this.totalEmployees > 0) {
      return;
    }

    this._setIsLoading(true);
    await requestApplyUserAction(this._periodId, USER_ACTIONS.BYPASS_CENSUS);
    runInAction(() => {
      this._setIsLoading(false);
      this.close();
      this._onRedirect();
    });
  }

  constructor({ groupId, periodId, onRedirect }: BypassModalDelegateParams) {
    super();
    this._groupId = groupId;
    this._periodId = periodId;
    this._onRedirect = onRedirect;

    makeObservable<BypassModalDelegate, '_totalEmployees' | '_isLoading' | '_setIsLoading' | '_resetEmployeeCount'>(
      this,
      {
        _totalEmployees: observable,
        _isLoading: observable,
        _setIsLoading: action,
        _resetEmployeeCount: action,
        isModalLoading: computed,
        totalEmployees: computed,
        showBypassBlockedAlert: computed,
        open: override,
        close: override,
        loadEmployeeCount: action.bound,
        confirmBypass: action.bound,
      },
    );
  }
}
